import { Button, Typography } from '@mui/material';
import cn from 'classnames';
import { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import { CircleBranding, CircleBrandingButtonShape } from 'src/api/circles';
import { filesApi } from 'src/api/files';
import {
  brandFontOptionsFor,
  brandPresetFor,
  brandingFromPreset,
  findBrandFontOption,
  hasBranding,
  isHexColor,
  resolveBranding,
} from 'src/branding';
import { UploadImage } from 'src/components/common/UploadImage/UploadImage';
import MaterialTextField from 'src/components/material/textField';
import styles from './BrandingSection.module.scss';

interface BrandingSectionProps {
  circleName: string;
  specialCode?: string;
  value: CircleBranding | null;
  onChange: (next: CircleBranding | null) => void;
}

const LOGO_URL_POLL_INTERVAL_MS = 1500;
const LOGO_URL_POLL_MAX_ATTEMPTS = 20;

/**
 * Waits for the S3 upload trigger to write the public https URL onto the
 * file record. The uploader itself only returns the file id.
 */
const waitForFileUrl = async (fileId: string): Promise<string | null> => {
  for (let attempt = 0; attempt < LOGO_URL_POLL_MAX_ATTEMPTS; attempt += 1) {
    try {
      const file = await filesApi.getFile(fileId, { fresh: true });
      if (file.url && file.url.startsWith('https://')) {
        return file.url;
      }
    } catch {
      // keep polling; the record may not be readable yet
    }
    await new Promise((resolve) =>
      setTimeout(resolve, LOGO_URL_POLL_INTERVAL_MS)
    );
  }
  return null;
};

type ColorKey =
  | 'primary_color'
  | 'on_primary_color'
  | 'secondary_color'
  | 'background_color'
  | 'text_color';

const COLOR_FIELDS: Array<{
  key: ColorKey;
  label: string;
  hint: string;
  /** Shown in the picker and as placeholder while the field is empty. */
  fallback: string;
}> = [
  {
    key: 'primary_color',
    label: 'Header',
    hint: 'Top bar, circle header and tabs.',
    fallback: '#ffffff',
  },
  {
    key: 'on_primary_color',
    label: 'Header text',
    hint: 'Text and icons on the header. Empty = black or white, whichever reads best.',
    fallback: '#191920',
  },
  {
    key: 'secondary_color',
    label: 'Buttons and links',
    hint: 'Empty = same as the header color.',
    fallback: '#f0544f',
  },
  {
    key: 'background_color',
    label: 'Page background',
    hint: 'Behind the circle content.',
    fallback: '#ffffff',
  },
  {
    key: 'text_color',
    label: 'Text',
    hint: 'Body text on the page background.',
    fallback: '#191920',
  },
];

const BUTTON_SHAPES: Array<{
  value: CircleBrandingButtonShape;
  label: string;
}> = [
  { value: 'rounded', label: 'Rounded' },
  { value: 'pill', label: 'Pill' },
];

/** `<input type="color">` only accepts the six-digit form. */
const toPickerHex = (hex: string): string => {
  const value = hex.trim().toLowerCase();
  return value.length === 4
    ? `#${value[1]}${value[1]}${value[2]}${value[2]}${value[3]}${value[3]}`
    : value;
};

/**
 * Leader-facing "Branding" section of Edit Circle. Keeps a draft of the
 * branding object; the parent form sends it with the PATCH only when changed,
 * so circles without branding never get the field.
 */
export const BrandingSection: FC<BrandingSectionProps> = ({
  circleName,
  specialCode,
  value,
  onChange,
}) => {
  const [logoStatus, setLogoStatus] = useState<string>('');
  const [hexInputs, setHexInputs] = useState<Record<string, string>>({});
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const draft: CircleBranding = value ?? {};
  const resolved = resolveBranding(draft);
  const fontOptions = brandFontOptionsFor(specialCode, draft);
  const preset = brandPresetFor(specialCode);
  const currentFont = findBrandFontOption(draft);
  // A font stack that is not one of our options (set through the API) is kept
  const hasCustomFont = Boolean(draft.font_family) && !currentFont;

  const update = (patch: Partial<CircleBranding>) => {
    const next: CircleBranding = { ...draft, ...patch };
    (Object.keys(next) as (keyof CircleBranding)[]).forEach((key) => {
      if (!next[key]) {
        delete next[key];
      }
    });
    onChange(hasBranding(next) ? next : null);
  };

  const handleDisplayName = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => update({ display_name: event.target.value });

  const handleColorPicker =
    (key: ColorKey) => (event: ChangeEvent<HTMLInputElement>) => {
      setHexInputs((prev) => ({ ...prev, [key]: event.target.value }));
      update({ [key]: event.target.value });
    };

  const handleHexText =
    (key: ColorKey) => (event: ChangeEvent<HTMLInputElement>) => {
      const text = event.target.value;
      setHexInputs((prev) => ({ ...prev, [key]: text }));
      if (text.trim() === '') {
        update({ [key]: undefined });
      } else if (isHexColor(text)) {
        update({ [key]: text.trim().toLowerCase() });
      }
    };

  const handleFont = (event: ChangeEvent<HTMLSelectElement>) => {
    const option = fontOptions.find((item) => item.id === event.target.value);
    update({
      font_family: option?.body,
      heading_font_family: option?.heading,
    });
  };

  const handleLogoUpload = async (fileId: string) => {
    setLogoStatus('Processing logo...');
    const url = await waitForFileUrl(fileId);
    if (!isMounted.current) {
      return;
    }
    if (url) {
      setLogoStatus('');
      update({ logo_url: url });
    } else {
      setLogoStatus(
        'The logo is still processing. Save again in a minute or re-upload.'
      );
    }
  };

  const handleApplyPreset = () => {
    if (!preset) {
      return;
    }
    setHexInputs({});
    setLogoStatus('');
    onChange(brandingFromPreset(preset, window.location.origin));
  };

  const handleRemove = () => {
    setHexInputs({});
    setLogoStatus('');
    onChange(null);
  };

  // Preview: the same header zone + page split the circle page uses
  const headerBackground = resolved.hasHeaderSurface
    ? resolved.primaryColor
    : '#ffffff';
  const headerText = resolved.hasHeaderSurface
    ? resolved.onPrimaryColor
    : '#191920';
  const buttonRadius = resolved.buttonShape === 'pill' ? 9999 : 8;

  return (
    <div className={styles.container}>
      <div className={styles.title}>Branding (optional)</div>
      <div className={styles.description}>
        Give this circle its own logo, colors and type so it feels like part of
        your own website. Members see it on the circle pages only; the rest of
        the app stays Dunbar. Leave everything empty to keep the Dunbar look.
      </div>

      {preset && (
        <div className={styles.presetRow}>
          <Button variant="outlined" onClick={handleApplyPreset}>
            {preset.label}
          </Button>
          <span className={styles.fieldHint}>
            Fills in the logo, colors, fonts and style below in one go. Click
            Save at the bottom to apply them.
          </span>
        </div>
      )}

      <div className={styles.field}>
        <div className={styles.fieldLabel}>Display name</div>
        <MaterialTextField
          variant="outlined"
          color="primary"
          placeholder={circleName || 'Organization name'}
          value={draft.display_name ?? ''}
          maxLength={80}
          onChange={handleDisplayName}
        />
      </div>

      <div className={styles.field}>
        <div className={styles.fieldLabel}>Logo</div>
        <div className={styles.fieldHint}>
          PNG or JPG, max 4MB. Shown in the top bar instead of the Dunbar logo.
          Use a version that reads well on your header color.
        </div>
        <UploadImage
          hideDesc
          title=""
          commentType="image-avatar"
          classNameImageContainer={styles.logoUpload}
          backgroundColor={
            resolved.hasHeaderSurface ? resolved.primaryColor : undefined
          }
          defaultImage={draft.logo_url ?? ''}
          onUpdate={(fileId) => {
            void handleLogoUpload(fileId);
          }}
        />
        {logoStatus && <div className={styles.fieldHint}>{logoStatus}</div>}
      </div>

      <div className={styles.field}>
        <div className={styles.fieldLabel}>Colors</div>
        <div className={styles.colorGrid}>
          {COLOR_FIELDS.map(({ key, label, hint, fallback }) => {
            const current = draft[key] ?? '';
            const pickerValue = isHexColor(current)
              ? toPickerHex(current)
              : fallback;
            return (
              <div className={styles.colorField} key={key}>
                <div className={styles.colorLabel}>{label}</div>
                <div className={styles.colorRow}>
                  <input
                    type="color"
                    className={styles.colorPicker}
                    aria-label={label}
                    value={pickerValue}
                    onChange={handleColorPicker(key)}
                  />
                  <input
                    type="text"
                    className={styles.hexInput}
                    aria-label={`${label} hex`}
                    placeholder="not set"
                    value={hexInputs[key] ?? current}
                    maxLength={7}
                    onChange={handleHexText(key)}
                  />
                </div>
                <div className={styles.colorHint}>{hint}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className={styles.field}>
        <div className={styles.fieldLabel}>Style</div>
        <div className={styles.styleRow}>
          <label className={styles.styleItem}>
            <span className={styles.colorLabel}>Font</span>
            <select
              className={styles.select}
              aria-label="Font"
              value={currentFont?.id ?? (hasCustomFont ? 'custom' : '')}
              onChange={handleFont}
            >
              <option value="">Dunbar default</option>
              {fontOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
              {hasCustomFont && (
                <option value="custom" disabled>
                  Custom
                </option>
              )}
            </select>
          </label>

          <div className={styles.styleItem}>
            <span className={styles.colorLabel}>Buttons</span>
            <div className={styles.segmented} role="group" aria-label="Buttons">
              {BUTTON_SHAPES.map(({ value: shape, label }) => (
                <button
                  key={shape}
                  type="button"
                  className={cn(styles.segment, {
                    [styles.segmentActive]: resolved.buttonShape === shape,
                  })}
                  aria-pressed={resolved.buttonShape === shape}
                  onClick={() =>
                    update({
                      button_shape: shape === 'pill' ? 'pill' : undefined,
                    })
                  }
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <label className={cn(styles.styleItem, styles.checkboxItem)}>
            <input
              type="checkbox"
              checked={resolved.headingUppercase}
              onChange={(event) =>
                update({ heading_uppercase: event.target.checked })
              }
            />
            <span className={styles.colorLabel}>Uppercase headings</span>
          </label>
        </div>
        <label
          className={cn(styles.styleItem, styles.checkboxItem, {
            [styles.checkboxDisabled]: !resolved.hasHeaderSurface,
          })}
        >
          <input
            type="checkbox"
            checked={draft.brand_navigation === true}
            disabled={!resolved.hasHeaderSurface}
            onChange={(event) =>
              update({ brand_navigation: event.target.checked })
            }
          />
          <span className={styles.colorLabel}>
            Color the app navigation too (sidebar and circle strip) while this
            circle is open
          </span>
        </label>
        {!resolved.hasHeaderSurface && (
          <div className={styles.colorHint}>
            Needs a header color. Without this option the navigation stays
            Dunbar.
          </div>
        )}
      </div>

      <div className={styles.field}>
        <div className={styles.fieldLabel}>Preview</div>
        <div
          className={styles.preview}
          style={resolved.hasFont ? { fontFamily: resolved.fontFamily } : {}}
        >
          <div
            className={styles.previewHeader}
            style={{ background: headerBackground, color: headerText }}
          >
            <div className={styles.previewBar}>
              {resolved.logoUrl ? (
                <img
                  className={styles.previewLogo}
                  src={resolved.logoUrl}
                  alt={resolved.displayName}
                />
              ) : (
                <span className={styles.previewName}>
                  {resolved.displayName}
                </span>
              )}
            </div>
            <div
              className={styles.previewTitle}
              style={{
                ...(resolved.hasHeadingFont && {
                  fontFamily: resolved.headingFontFamily,
                }),
                textTransform: resolved.headingUppercase ? 'uppercase' : 'none',
              }}
            >
              {circleName || 'Circle name'}
            </div>
            <div className={styles.previewActions}>
              <span
                className={styles.previewButton}
                style={{
                  background: resolved.accentColor,
                  color: resolved.onAccentColor,
                  borderRadius: buttonRadius,
                }}
              >
                Add Challenge
              </span>
              <span
                className={styles.previewButton}
                style={{
                  border: `1px solid ${headerText}`,
                  borderRadius: buttonRadius,
                }}
              >
                Members
              </span>
            </div>
            <div className={styles.previewTabs}>
              <span
                className={styles.previewTabActive}
                style={{ borderColor: resolved.accentColor }}
              >
                Challenges
              </span>
              <span className={styles.previewTab}>Information</span>
              <span className={styles.previewTab}>Messages</span>
            </div>
          </div>
          <div
            className={styles.previewBody}
            style={{
              background: resolved.backgroundColor,
              color: resolved.textColor,
            }}
          >
            <span>Circle content looks like this, with </span>
            <span
              className={styles.previewLink}
              style={{ color: resolved.accentTextColor }}
            >
              links like this
            </span>
            <span>.</span>
          </div>
        </div>
      </div>

      {hasBranding(draft) && (
        <Button
          variant="text"
          color="error"
          size="small"
          onClick={handleRemove}
          sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
        >
          Remove branding (back to Dunbar)
        </Button>
      )}
      <Typography variant="caption" className={styles.footnote}>
        Saved together with the rest of the circle.
      </Typography>
    </div>
  );
};
